/**
 * The second factor.
 *
 * Shown between a password sign-in and the shell whenever the account requires MFA and the session has not
 * reached aal2. Two states: enrol (no verified factor yet — show the QR) and verify (a factor exists — ask
 * for the code). Either way the shell stays closed, which is not this component's decision to make: at
 * aal1 an MFA-required account holds no permission at all, and every command refuses with
 * RTC_MFA_REQUIRED. This screen exists so that refusal is explained *before* it happens, not after.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { useSession } from '../lib/session';
import { beginTotpEnrolment, verifiedTotpFactorId, verifyTotp, type Enrolment } from '../lib/auth';
import { Button, SectionLabel } from '../ui/primitives';
import { Field, Input } from '../ui/forms';
import { AuthError, AuthFrame } from './AuthFrame';

export function MfaGate() {
  const { me, aal, refresh, signOut } = useSession();
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const factor = useQuery({ queryKey: ['mfa-factor'], queryFn: verifiedTotpFactorId, retry: false });
  const needsEnrolment = factor.data === null && !aal?.hasVerifiedFactor;

  useEffect(() => {
    if (!needsEnrolment || enrolment) return;
    let cancelled = false;
    beginTotpEnrolment()
      .then((e) => { if (!cancelled) setEnrolment(e); })
      .catch((e) => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, [needsEnrolment, enrolment]);

  const submit = async () => {
    const factorId = enrolment?.factorId ?? factor.data;
    if (!factorId) return;
    setBusy(true);
    setError(null);
    try {
      await verifyTotp(factorId, code);
      await refresh();
    } catch (err) {
      setError(err);
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame
      title={needsEnrolment ? 'Set up two-factor authentication' : 'Enter your code'}
      subtitle={
        needsEnrolment
          ? 'This account requires a second factor. Scan the code with an authenticator app, then enter the six digits it shows.'
          : `Signed in as ${me?.email ?? 'you'}. Open your authenticator app and enter the current code.`
      }
      aside={
        <>
          <ShieldCheck size={12} className="mr-1 inline" aria-hidden />
          Until the code is verified this session holds no permission. That is enforced by the database, not by this page.
        </>
      }
    >
      {needsEnrolment && enrolment ? (
        <div className="mb-4 flex gap-4">
          <img src={enrolment.qrCode} alt="QR code for the authenticator app" width={148} height={148} className="shrink-0 rounded-[var(--radius-control)] border border-line bg-white p-1" />
          <div className="min-w-0">
            <SectionLabel>Cannot scan?</SectionLabel>
            <p className="mt-1 text-small text-ink-muted">Enter this key by hand instead:</p>
            <code className="code-text mt-1 block break-all">{enrolment.secret}</code>
          </div>
        </div>
      ) : null}

      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        className="space-y-4"
      >
        <Field label="Six-digit code" required hint="Codes change every 30 seconds.">
          <Input
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9 ]*"
            maxLength={7}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            height="form"
            autoFocus
            className="font-mono tracking-[0.3em]"
          />
        </Field>

        <AuthError error={error} />

        <div className="flex items-center justify-between">
          <Button type="button" variant="ghost" onClick={() => void signOut()} disabled={busy}>
            Sign out
          </Button>
          <Button type="submit" variant="primary" disabled={busy || code.replace(/\s/g, '').length !== 6 || (needsEnrolment && !enrolment)}>
            {busy ? 'Checking…' : needsEnrolment ? 'Verify and finish setup' : 'Verify'}
          </Button>
        </div>
      </form>
    </AuthFrame>
  );
}
